import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listStoriesAdmin, deleteStory } from "@/lib/api/stories.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/stories/")({
  loader: () => listStoriesAdmin({ data: {} }),
  component: AdminStoriesList,
});

type Row = Awaited<ReturnType<typeof listStoriesAdmin>>[number];

function buildColumns(onDelete: (row: Row) => Promise<void>): ColumnDef<Row>[] {
  return [
  {
    accessorKey: "title",
    header: "Title",
    cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
  },
  { accessorKey: "client", header: "Client", meta: { filterVariant: "text" } },
  {
    accessorKey: "industry",
    header: "Industry",
    meta: { filterVariant: "select", groupable: true },
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { filterVariant: "select", groupable: true },
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    ...tableActionsColumn<Row>(),
    cell: ({ row }) => (
      <TableRowActions
        edit={{ to: "/admin/stories/$id", params: { id: row.original.id } }}
        onDelete={() => onDelete(row.original)}
        deleteLabel={row.original.title}
      />
    ),
  },
];
}

function AdminStoriesList() {
  const data = Route.useLoaderData();
  const router = useRouter();

  async function handleDelete(row: Row) {
    try {
      await deleteStory({ data: { id: row.id } });
      toast.success("Story deleted");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns = buildColumns(handleDelete);

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Client stories"
        action={
          <Button asChild>
            <Link to="/admin/stories/new">
              <Plus className="mr-2 h-4 w-4" /> New story
            </Link>
          </Button>
        }
      />
      <DataTable tableId="stories" columns={columns} data={data} />
    </AdminPageShell>
  );
}
